import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { SOURCES, LEAD_STATUSES } from '../lib/format';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2 } from 'lucide-react';
import { useQuickAddLead } from '../hooks/useQuickAddLead';
import { DuplicateAlertDialog } from './DuplicateAlertDialog';

const NONE = '__none__';
const QUICK_ADD_STATUSES = LEAD_STATUSES.filter((s) => s !== 'Lost');

const FieldBox = ({ label, error, children }) => (
  <div className="space-y-1">
    <Label className="text-xs">{label}</Label>
    {children}
    {error && <p className="text-[11px] text-destructive" role="alert">{error}</p>}
  </div>
);

// Nullable select — collapses the repeated Select/NONE pattern
const NullableSelect = ({ label, value, onChange, options, placeholder, testId }) => (
  <FieldBox label={label}>
    <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? null : v)}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>—</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </FieldBox>
);

const errCls = (has) => (has ? 'border-destructive focus-visible:ring-destructive' : '');

const ContactFields = ({ form, set, errors, suggest }) => (
  <>
    <FieldBox label="Lead Name *" error={errors.lead_name}>
      <Input autoFocus value={form.lead_name} onChange={(e) => set('lead_name', e.target.value)} placeholder="Contact person" className={errCls(errors.lead_name)} data-testid="quick-add-name-input" />
    </FieldBox>
    <div className="grid grid-cols-2 gap-3">
      <FieldBox label="Company / Brand *" error={errors.company_name}>
        <Input value={form.company_name} onChange={(e) => set('company_name', e.target.value)} className={errCls(errors.company_name)} data-testid="quick-add-company-input" />
      </FieldBox>
      <FieldBox label="Mobile Number *" error={errors.phone}>
        <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="10-digit mobile" className={errCls(errors.phone)} data-testid="quick-add-phone-input" />
      </FieldBox>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <FieldBox label="Category">
        <Input value={form.category} onChange={(e) => set('category', e.target.value)} placeholder="e.g. Fashion, Realty" list="quick-add-category-suggest" data-testid="quick-add-category-input" />
        <datalist id="quick-add-category-suggest">
          {suggest.categories.map((c) => <option key={c} value={c} />)}
        </datalist>
      </FieldBox>
      <FieldBox label="City">
        <Input value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="e.g. Mumbai" list="quick-add-city-suggest" data-testid="quick-add-city-input" />
        <datalist id="quick-add-city-suggest">
          {suggest.cities.map((c) => <option key={c} value={c} />)}
        </datalist>
      </FieldBox>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <FieldBox label="Email">
        <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} data-testid="quick-add-email-input" />
      </FieldBox>
      <NullableSelect label="Lead Source" value={form.source} onChange={(v) => set('source', v)} options={SOURCES} placeholder="Source" testId="quick-add-source-select" />
    </div>
  </>
);

const AssignmentFields = ({ form, set, members }) => {
  const teams = [...new Set(members.flatMap((m) => m.teams || []))];
  const memberOpts = members.map((m) => ({ value: m.id, label: m.name }));
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <NullableSelect label="Setter" value={form.setter_id} onChange={(v) => set('setter_id', v)} options={memberOpts} placeholder="Setter" testId="quick-add-setter-select" />
        <NullableSelect label="Closer" value={form.closer_id} onChange={(v) => set('closer_id', v)} options={memberOpts} placeholder="Closer" testId="quick-add-closer-select" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <NullableSelect label="Team" value={form.team} onChange={(v) => set('team', v)} options={teams} placeholder="Team" testId="quick-add-team-select" />
        <FieldBox label="Expected Value (₹)">
          <Input type="number" value={form.total_deal_value} onChange={(e) => set('total_deal_value', e.target.value)} data-testid="quick-add-value-input" />
        </FieldBox>
      </div>
    </>
  );
};

const StatusFields = ({ form, set }) => (
  <div className="grid grid-cols-2 gap-3">
    <FieldBox label="Status">
      <Select value={form.status} onValueChange={(v) => set('status', v)}>
        <SelectTrigger data-testid="quick-add-status-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          {QUICK_ADD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
        </SelectContent>
      </Select>
    </FieldBox>
    <FieldBox label="Next Follow-up Date">
      <Input type="date" value={form.next_followup_date} onChange={(e) => set('next_followup_date', e.target.value)} data-testid="quick-add-followup-input" />
    </FieldBox>
  </div>
);

const LeadFormFields = ({ form, set, errors, members, suggest }) => (
  <div className="space-y-3">
    <ContactFields form={form} set={set} errors={errors} suggest={suggest} />
    <AssignmentFields form={form} set={set} members={members} />
    <StatusFields form={form} set={set} />
  </div>
);

const EMPTY_SUGGEST = { categories: [], cities: [] };

export const QuickAddLead = ({ open, onClose, members, onCreated }) => {
  const { form, set, errors, saving, submit, createLead, dupMatches, dismissDuplicates } = useQuickAddLead({ onCreated, onClose });
  const [suggest, setSuggest] = useState(EMPTY_SUGGEST);

  // Auto-suggest Category/City from existing values (loaded once per dialog open)
  useEffect(() => {
    if (!open || suggest.categories.length || suggest.cities.length) return;
    api.get('/leads-meta')
      .then(({ data }) => setSuggest({ categories: data.categories || [], cities: data.cities || [] }))
      .catch(() => {});
  }, [open, suggest]);

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="quick-add-dialog">
          <DialogHeader><DialogTitle className="font-heading">Add Lead</DialogTitle></DialogHeader>
          <LeadFormFields form={form} set={set} errors={errors} members={members} suggest={suggest} />
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="quick-add-submit-button">
              {saving ? <Loader2 className="animate-spin" size={15} /> : 'Add Lead'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DuplicateAlertDialog matches={dupMatches} saving={saving} onCancel={dismissDuplicates} onConfirm={createLead} />
    </>
  );
};
